import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Orders from "@/pages/Orders";
import NewOrder from "@/pages/NewOrder";
import OrderDetail from "@/pages/OrderDetail";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import CustomerPortal from "@/pages/CustomerPortal";
import PublicTracking from "@/pages/PublicTracking";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import OfferPopupSettings from "@/pages/OfferPopupSettings";
import ShippingSettings from "@/pages/ShippingSettings";
import Enquiries from "@/pages/Enquiries";
import Media from "@/pages/Media";
import Quotations from "@/pages/Quotations";
import NewQuotation from "@/pages/NewQuotation";
import QuotationDetail from "@/pages/QuotationDetail";
import Production from "@/pages/Production";
import Dispatch from "@/pages/Dispatch";
import EditOrder from "@/pages/EditOrder";
import Testimonials from "@/pages/Testimonials";
import Vacancies from "@/pages/Vacancies";
import ChangePassword from "@/pages/ChangePassword";
import AuthCallback from "@/pages/AuthCallback";
import HeroSlides from "@/pages/HeroSlides";
import Offers from "@/pages/Offers";
import Catalog from "@/pages/Catalog";
import Coupons from "@/pages/Coupons";
import GiftCards from "@/pages/GiftCards";
import ShopOrders from "@/pages/ShopOrders";
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import Products from "@/pages/public/Products";
import ProductDetail from "@/pages/public/ProductDetail";
import CartPage from "@/pages/public/Cart";
import Checkout from "@/pages/public/Checkout";
import OrderConfirmation from "@/pages/public/OrderConfirmation";
import Portfolio from "@/pages/public/Portfolio";
import Contact from "@/pages/public/Contact";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import ShippingDelivery from "@/pages/public/ShippingDelivery";
import ReturnsRefund from "@/pages/public/ReturnsRefund";
import Careers from "@/pages/public/Careers";
import CustomerOrders from "@/pages/customer/CustomerOrders";
import CustomerPayments from "@/pages/customer/CustomerPayments";
import CustomerQuotations from "@/pages/customer/CustomerQuotations";
import CustomerEnquiries from "@/pages/customer/CustomerEnquiries";
import { Toaster } from "sonner";
import "@/App.css";

// Intercept Emergent Google auth callback which arrives as `#session_id=...`
function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing site */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/shipping-delivery" element={<ShippingDelivery />} />
        <Route path="/returns-refund" element={<ReturnsRefund />} />
        <Route path="/careers" element={<Careers />} />
      </Route>

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/track" element={<PublicTracking />} />
      <Route path="/track/:id" element={<PublicTracking />} />
      <Route path="/change-password" element={
        <ProtectedRoute><ChangePassword /></ProtectedRoute>
      } />

      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/new" element={<NewOrder />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/orders/:id/edit" element={<EditOrder />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/offer-popup" element={<OfferPopupSettings />} />
        <Route path="/shipping" element={<ShippingSettings />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/enquiries" element={<Enquiries />} />
        <Route path="/media" element={<Media />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/vacancies" element={<Vacancies />} />
        <Route path="/hero-slides" element={<HeroSlides />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/coupons" element={<Coupons />} />
        <Route path="/giftcards" element={<GiftCards />} />
        <Route path="/shop-orders" element={<ShopOrders />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/quotations/new" element={<NewQuotation />} />
        <Route path="/quotations/:id" element={<QuotationDetail />} />
        <Route path="/production" element={<Production />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/portal" element={<CustomerPortal />} />
        <Route path="/my/orders" element={<CustomerOrders />} />
        <Route path="/my/payments" element={<CustomerPayments />} />
        <Route path="/my/quotations" element={<CustomerQuotations />} />
        <Route path="/my/enquiries" element={<CustomerEnquiries />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
